import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useI18n } from '../context/I18nContext';

const SunMoonLines = ({ latitude, longitude, sunTimes, visible = true }) => {
  const { t } = useI18n();
  const map = useMap();
  const linesRef = useRef([]);

  useEffect(() => {
    // Clear existing lines
    linesRef.current.forEach(line => map.removeLayer(line));
    linesRef.current = [];

    if (!visible || !sunTimes) return;

    const center = [latitude, longitude];
    const lineLength = 0.15; // Degree length for lines

    // Calculate sun azimuth for different times
    const calculateAzimuth = (timeStr) => {
      if (!timeStr) return null;
      const [hours, minutes] = timeStr.split(':').map(Number);
      const hour = hours + minutes / 60;
      
      const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
      const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
      
      const solarNoon = 12.0 - longitude / 15.0;
      const hourAngle = 15 * (hour - solarNoon);
      
      const latRad = latitude * Math.PI / 180;
      const decRad = declination * Math.PI / 180;
      const haRad = hourAngle * Math.PI / 180;
      
      const sinAlt = Math.sin(latRad) * Math.sin(decRad) + 
                     Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
      const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
      
      const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitude)) / 
                    (Math.cos(latRad) * Math.cos(altitude) + 0.0001);
      let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
      
      if (hourAngle > 0) azimuth = 360 - azimuth;
      
      return azimuth;
    };

    // Create line from center in direction of azimuth
    const createLine = (azimuth, color, weight, dashArray = null) => {
      if (azimuth === null) return null;
      
      const azRad = (azimuth - 90) * Math.PI / 180; // Adjust for map orientation
      const endLat = latitude + lineLength * Math.cos(azRad);
      const endLng = longitude + lineLength * Math.sin(azRad) / Math.cos(latitude * Math.PI / 180);
      
      const line = L.polyline([center, [endLat, endLng]], {
        color,
        weight,
        opacity: 0.8,
        dashArray
      }).addTo(map);
      
      return line;
    };

    // Sunrise line (yellow, solid)
    const sunriseAz = calculateAzimuth(sunTimes.sunrise);
    if (sunriseAz) {
      const line = createLine(sunriseAz, '#F59E0B', 3);
      if (line) {
        line.bindTooltip(`${t('sun.sunrise')}: ${sunTimes.sunrise}`, { permanent: false, direction: 'top' });
        linesRef.current.push(line);
      }
    }

    // Sunset line (orange, solid)
    const sunsetAz = calculateAzimuth(sunTimes.sunset);
    if (sunsetAz) {
      const line = createLine(sunsetAz, '#F97316', 3);
      if (line) {
        line.bindTooltip(`${t('sun.sunset')}: ${sunTimes.sunset}`, { permanent: false, direction: 'top' });
        linesRef.current.push(line);
      }
    }

    // Golden hour morning end (yellow, dashed)
    if (sunTimes.golden_hour_morning?.end) {
      const az = calculateAzimuth(sunTimes.golden_hour_morning.end);
      if (az) {
        const line = createLine(az, '#FBBF24', 2, '5, 5');
        if (line) {
          line.bindTooltip(`${t('sunmoon.goldenHourEnd')}: ${sunTimes.golden_hour_morning.end}`, { permanent: false });
          linesRef.current.push(line);
        }
      }
    }

    // Golden hour evening start (orange, dashed)
    if (sunTimes.golden_hour_evening?.start) {
      const az = calculateAzimuth(sunTimes.golden_hour_evening.start);
      if (az) {
        const line = createLine(az, '#FB923C', 2, '5, 5');
        if (line) {
          line.bindTooltip(`${t('sunmoon.goldenHourStart')}: ${sunTimes.golden_hour_evening.start}`, { permanent: false });
          linesRef.current.push(line);
        }
      }
    }

    // Blue hour lines
    if (sunTimes.blue_hour_morning?.start) {
      const az = calculateAzimuth(sunTimes.blue_hour_morning.start);
      if (az) {
        const line = createLine(az, '#3B82F6', 2, '3, 3');
        if (line) {
          line.bindTooltip(`${t('sun.blueHour')}: ${sunTimes.blue_hour_morning.start}`, { permanent: false });
          linesRef.current.push(line);
        }
      }
    }

    if (sunTimes.blue_hour_evening?.end) {
      const az = calculateAzimuth(sunTimes.blue_hour_evening.end);
      if (az) {
        const line = createLine(az, '#1D4ED8', 2, '3, 3');
        if (line) {
          line.bindTooltip(`${t('sunmoon.blueHourEnd')}: ${sunTimes.blue_hour_evening.end}`, { permanent: false });
          linesRef.current.push(line);
        }
      }
    }

    // Cleanup on unmount
    return () => {
      linesRef.current.forEach(line => map.removeLayer(line));
    };
  }, [map, latitude, longitude, sunTimes, visible, t]);

  return null;
};

export default SunMoonLines;
